import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Renderer2,
  computed,
  inject,
  input,
  viewChild,
  output,
} from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { Bind } from '@openng/optimus-ui/bind';
import { Button } from '@openng/optimus-ui/button';

interface IFileUpload {
  file: File;
  progress: number;
  url: string | null;
  type: string;
  size: string;
  name: string;
  uploadId: number;
}

export interface IFileExtension {
  extensions: string[];
  mimeTypes: string[];
}

@Component({
  selector: 'ag1-upload',
  templateUrl: './ag1-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule, Tooltip, Bind, Button],
})
export class Ag1UploadComponent {
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);

  // (Required)
  // Default supported file types are supplied as an example
  // Find your appropriate file types here:
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#unique_file_type_specifiers
  readonly supportedFileTypes = input<IFileExtension[]>([
    {
      extensions: ['.pdf'],
      mimeTypes: ['application/pdf'],
    },
    {
      extensions: ['.jpg', '.jpeg'],
      mimeTypes: ['image/jpeg'],
    },
    {
      extensions: ['.png'],
      mimeTypes: ['image/png'],
    },
    {
      extensions: ['.doc', '.docx'],
      mimeTypes: [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
    },
    {
      extensions: ['.xls', '.xlsx'],
      mimeTypes: [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ],
    },
    // EDI examples
    {
      extensions: ['.*'], // Accept any extension
      mimeTypes: [
        'text/*', // Accept any text file
        'text/csv',
        'application/json',
        'application/x-json',
        'application/octet-stream',
        'text/x-edi', // Some systems use this for EDI
        'application/edi-x12', // Some systems use this for EDI
        'application/edifact', // Some systems use this for EDI
      ],
    },
  ]);

  // (optional) Set to false to allow only one file to be uploaded at a time
  readonly multiple = input(true);

  readonly supportedFileTypesString = computed(() => {
    return this.supportedFileTypes()
      .map((type: IFileExtension) => type.extensions.join(', '))
      .join(', ');
  });

  // Returns the selected files
  readonly filesSelected = output<File[]>();

  readonly dropZone = viewChild.required<ElementRef>('dropZone');

  files: IFileUpload[] = [];
  private nextUploadId = 0; // Counter for unique upload IDs

  readonly validDropZoneClasses: readonly string[] = [
    'bg-french-blue-50/30!',
  ] as const;
  readonly invalidDropZoneClasses: readonly string[] = [
    'bg-red-50/30!',
    'border-red-500!',
  ] as const;

  constructor() {}

  onFileSelect(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.items) {
      const isValid: boolean = Array.from(event.dataTransfer.items).every(
        (item: DataTransferItem) => {
          if (item.kind === 'file') {
            const filename = item.getAsFile()?.name || '';
            return (
              this.isValidMimeType(item.type) ||
              this.isValidFileExtension(filename)
            );
          }
          return false;
        },
      );

      const hasAvailableSlots: boolean =
        this.multiple() || this.files.length === 0;
      this.updateDropZoneClasses(isValid && hasAvailableSlots);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.resetDropZoneClasses();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.resetDropZoneClasses();

    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  handleFiles(fileList: FileList): void {
    const filesToProcess: File[] = this.multiple()
      ? Array.from(fileList)
      : [fileList[0]];

    if (!this.multiple() && this.files.length > 0) {
      // If multiple is false and there's already a file, don't process new ones
      return;
    }

    filesToProcess.forEach((file: File) => {
      if (
        (this.isValidMimeType(file.type) ||
          this.isValidFileExtension(file.name)) &&
        !this.isDuplicateFile(file)
      ) {
        const reader: FileReader = new FileReader();

        reader.onload = (e: ProgressEvent<FileReader>) => {
          const url: string = e.target?.result as string;
          const uploadedFile: IFileUpload = {
            file,
            progress: 0,
            url,
            type: this.formatFileExtension(file.name),
            size: this.formatFileSize(file.size),
            name: this.formatFileName(file.name),
            uploadId: this.nextUploadId++,
          };

          if (!this.multiple()) {
            // If multiple is false, replace the existing file
            this.files = [uploadedFile];
          } else {
            this.files.push(uploadedFile);
          }

          this.simulateUpload(uploadedFile.uploadId);
          this.emitFiles();
        };

        reader.readAsDataURL(file);
      }
    });
  }

  simulateUpload(uploadId: number): void {
    const interval = setInterval(() => {
      const fileIndex: number = this.files.findIndex(
        (f: IFileUpload) => f.uploadId === uploadId,
      );

      // end animation if file is removed
      if (fileIndex === -1) {
        clearInterval(interval);
        return;
      }

      this.files[fileIndex].progress++;
      this.refreshInterface();

      if (this.files[fileIndex].progress === 100) {
        clearInterval(interval);
      }
    }, 15);
  }

  onRemove(index: number): void {
    const file: IFileUpload = this.files[index];

    if (!file) {
      return;
    }

    URL.revokeObjectURL(file.url!);
    this.files.splice(index, 1);

    this.emitFiles();
    this.refreshInterface();
  }

  emitFiles(): void {
    const files: File[] = this.files.map((f: IFileUpload) => f.file);
    this.filesSelected.emit(files);
  }

  formatFileSize(bytes: number): string {
    // Format file size to human-readable format
    // eg 1024 -> 1 KB
    // eg 1048576 -> 1 MB
    // eg 1073741824 -> 1 GB etc.

    if (bytes === 0) {
      return '0 Bytes';
    }

    const k: number = 1024;
    const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i: number = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatFileExtension(name: string): string {
    // eg 'file.pdf' -> 'pdf'
    return name.split('.').pop() || '';
  }

  formatFileName(name: string): string {
    // eg 'file.pdf' -> 'file'
    return name.split('.').slice(0, -1).join('.');
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  isValidMimeType(mimeType: string): boolean {
    // Special case for text files and EDI files which might be detected as text/plain
    if (mimeType === 'text/plain' || mimeType === 'application/octet-stream') {
      return true;
    }

    return this.supportedFileTypes().some((fileType: IFileExtension) =>
      fileType.mimeTypes.some((supported) => {
        // Handle wildcards like 'text/*'
        if (supported.endsWith('/*')) {
          return mimeType.startsWith(supported.slice(0, -1));
        }
        return supported === mimeType;
      }),
    );
  }

  isValidFileExtension(filename: string): boolean {
    const ext = '.' + filename.split('.').pop()?.toLowerCase();
    return this.supportedFileTypes().some(
      (fileType) =>
        fileType.extensions.includes('.*') ||
        fileType.extensions.map((e) => e.toLowerCase()).includes(ext),
    );
  }

  isDuplicateFile(file: File): boolean {
    return this.files.some(
      (existingFile: IFileUpload) =>
        existingFile.file.name === file.name &&
        existingFile.file.type === file.type,
    );
  }

  updateDropZoneClasses(isValid: boolean): void {
    this.resetDropZoneClasses();

    const classesToAdd: readonly string[] = isValid
      ? this.validDropZoneClasses
      : this.invalidDropZoneClasses;
    classesToAdd.forEach((className: string) => {
      this.renderer.addClass(this.dropZone().nativeElement, className);
    });
  }

  resetDropZoneClasses(): void {
    [...this.validDropZoneClasses, ...this.invalidDropZoneClasses].forEach(
      (className: string) => {
        this.renderer.removeClass(this.dropZone().nativeElement, className);
      },
    );
  }

  get isDisabled(): boolean {
    return !this.multiple() && this.files.length > 0;
  }
}
